/**
 * @module VerifyScriptedConstants
 * @description Phase 10 extraction of scripted verify constants.
 * Keeps scripted-only mappings separate from shared and VoIP verify domains.
 */

import { t } from '@lingui/macro';

/** Fields to omit from scripted test result data display (from verifyScriptedTest.service.js) */
export const SCRIPTED_METRICS_OMIT_FIELDS = [
    'agentId',
    'isMonitoringTest',
    'type',
    'verify_test',
    'quick_test_id',
    'quick_test',
    'test_name',
    'test_type',
    'test_id',
    'test_guid',
    'test_time',
    'org_guid',
    'agent_guid',
    'scriptAvailability',
    'log_file',
    'availability',
    'test_error',
    'is_pass',
];

/** Scripted metric fields where negative values are allowed (not replaced with '--') */
export const SCRIPTED_ALLOW_NEGATIVE = ['wifi_signal_level_dbm'];

/**
 * Translate a scripted test error code to a human-readable message.
 * portal/ui source: i18n key config.tests.verify.scripted.errorCodes.<code>
 *
 * @param {number|string} errorCode
 * @returns {string}
 */
export const translateScriptedErrorCode = (errorCode) => {
    // Full error code map matching portal/ui: i18n key config.tests.verify.scripted.errorCodes
    const CODES = {
        0: t`No Error`,
        1: t`baseException`,
        2: t`exception`,
        3: t`standardError`,
        4: t`arithmeticError`,
        5: t`bufferError`,
        6: t`lookupError`,
        7: t`environmentError`,
        8: t`assertionError`,
        9: t`attributeError`,
        10: t`eofError`,
        11: t`floatingPointError`,
        12: t`generatorError`,
        13: t`ioError`,
        14: t`importError`,
        15: t`indexError`,
        16: t`keyError`,
        17: t`keyboardInterrupt`,
        18: t`memoryError`,
        19: t`nameError`,
        20: t`notImplementedError`,
        21: t`osError`,
        22: t`overflowError`,
        23: t`referenceError`,
        24: t`runtimeError`,
        25: t`stopIteration`,
        26: t`syntaxError`,
        27: t`indentationError`,
        28: t`tabError`,
        29: t`systemError`,
        30: t`systemExit`,
        31: t`typeError`,
        32: t`unboundLocalError`,
        33: t`unicodeError`,
        34: t`unicodeEncodeError`,
        35: t`unicodeDecodeError`,
        36: t`unicodeTranslateError`,
        37: t`valueError`,
        38: t`vmsError`,
        39: t`windowsError`,
        40: t`zeroDivisionError`,
        41: t`warning`,
        42: t`userWarning`,
        43: t`deprecationWarning`,
        44: t`pendingDeprecationWarning`,
        45: t`syntaxWarning`,
        46: t`runtimeWarning`,
        47: t`futureWarning`,
        48: t`importWarning`,
        49: t`unicodeWarning`,
    };
    return CODES[errorCode] ?? t`Unknown Error (${errorCode})`;
};
